/**
 * HeartbeatHistoryCard — F-036/suite#3 (Option A, spec-treuer Umbau)
 *
 * Ausklappbare Liste der letzten Heartbeats aus hotspot.heartbeats[]
 * (bereits im Show-Response GET /api/v2/hotspots/:id enthalten, siehe
 * useHotspotDetail.ts). Kein eigener Fetch mehr — die Card bekommt die
 * Daten direkt vom Parent (HotspotDetail.tsx).
 *
 * Spalten: Timestamp (relativ + ISO-Tooltip), Status (✓/✗), Lat/Lng,
 * Distance, Cell-Type.
 */
import { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import type { HotspotHeartbeat } from './useHotspotDetail'
import { activeBcp47, activeDateFnsLocale } from '../../lib/locale'

// --- Helpers ---

function relativeTime(iso: string): string {
  try {
    const date = new Date(iso)
    return formatDistanceToNow(date, { addSuffix: true, locale: activeDateFnsLocale() })
  } catch {
    return iso
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(activeBcp47(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

function formatCoord(value: number | null): string {
  if (value == null) return '—'
  return value.toFixed(5)
}

function formatDistance(meters: number | null): string {
  if (meters == null) return '—'
  // Ab 1 km lesbarer als km ausgeben (Distanzen können vierstellig+ sein).
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`
  return `${meters} m`
}

function formatCellType(cellType: string | null): string {
  return cellType ?? '—'
}

// --- HeartbeatRow ---

type HeartbeatRowProps = {
  heartbeat: HotspotHeartbeat
}

function HeartbeatRow({ heartbeat }: HeartbeatRowProps) {
  const { t } = useTranslation('hotspots')

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(50px, 0.6fr) repeat(2, minmax(70px, 0.8fr)) minmax(70px, 0.8fr) minmax(80px, 1fr)',
        gap: 8,
        padding: '10px 0',
        borderBottom: '1px solid var(--border)',
        alignItems: 'center',
        fontSize: 12,
      }}
    >
      {/* Timestamp */}
      <div title={heartbeat.timestamp}>
        <div style={{ fontWeight: 500 }}>{relativeTime(heartbeat.timestamp)}</div>
        <div className="muted" style={{ fontSize: 10, marginTop: 1 }}>
          {formatDate(heartbeat.timestamp)}
        </div>
      </div>

      {/* Status */}
      <div style={{ textAlign: 'center' }}>
        {heartbeat.online_status ? (
          <span
            aria-label={t('heartbeat.statusOnline')}
            title={t('heartbeat.statusOnline')}
            style={{ color: 'var(--success)', fontWeight: 700 }}
          >
            ✓
          </span>
        ) : (
          <span
            aria-label={t('heartbeat.statusOffline')}
            title={t('heartbeat.statusOffline')}
            style={{ color: 'var(--danger)', fontWeight: 700 }}
          >
            ✗
          </span>
        )}
      </div>

      {/* Lat */}
      <div style={{ textAlign: 'right' }}>{formatCoord(heartbeat.lat)}</div>

      {/* Lng */}
      <div style={{ textAlign: 'right' }}>{formatCoord(heartbeat.lng)}</div>

      {/* Distance */}
      <div style={{ textAlign: 'right' }}>{formatDistance(heartbeat.distance_to_asserted_m)}</div>

      {/* Cell Type */}
      <div style={{ textAlign: 'center' }}>{formatCellType(heartbeat.cell_type)}</div>
    </div>
  )
}

// --- Props ---

type Props = {
  heartbeats: HotspotHeartbeat[] | undefined
}

export function HeartbeatHistoryCard({ heartbeats }: Props) {
  const { t } = useTranslation('hotspots')
  const [isOpen, setIsOpen] = useState(false)

  const handleToggle = () => setIsOpen((open) => !open)

  return (
    <div className="card card-pad" aria-label={t('heartbeat.cardLabel')}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls="heartbeat-history-content"
        aria-label={isOpen ? t('heartbeat.toggleHide') : t('heartbeat.toggleShow')}
        data-testid="heartbeat-history-toggle"
        onClick={handleToggle}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 className="section-heading" style={{ margin: 0 }}>
          {t('heartbeat.heading')}
        </h3>
        <ChevronDown
          aria-hidden="true"
          size={20}
          strokeWidth={2}
          style={{
            marginLeft: 12,
            flexShrink: 0,
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : undefined,
          }}
        />
      </button>

      {/* Immer im DOM (hidden statt conditional), damit aria-controls des
          Toggle-Buttons auch eingeklappt auf ein existierendes Element zeigt. */}
      <div id="heartbeat-history-content" hidden={!isOpen} style={{ marginTop: 16 }}>
          {!heartbeats || heartbeats.length === 0 ? (
            <div className="muted" style={{ padding: '24px 0', textAlign: 'center' }}>
              {t('heartbeat.emptyMsg')}
            </div>
          ) : (
            <>
              {/* Header Row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(50px, 0.6fr) repeat(2, minmax(70px, 0.8fr)) minmax(70px, 0.8fr) minmax(80px, 1fr)',
                  gap: 8,
                  paddingBottom: 8,
                  borderBottom: '2px solid var(--border)',
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: 'var(--text-muted)',
                }}
              >
                <div>{t('heartbeat.colTime')}</div>
                <div style={{ textAlign: 'center' }}>{t('heartbeat.colStatus')}</div>
                <div style={{ textAlign: 'right' }}>{t('heartbeat.colLat')}</div>
                <div style={{ textAlign: 'right' }}>{t('heartbeat.colLng')}</div>
                <div style={{ textAlign: 'right' }}>{t('heartbeat.colDistance')}</div>
                <div style={{ textAlign: 'center' }}>{t('heartbeat.colCellType')}</div>
              </div>

              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {heartbeats.map((hb, index) => (
                  // Kein stabiles ID-Feld im Spec-Shape — Timestamp+Index als Key.
                  <HeartbeatRow key={`${hb.timestamp}-${index}`} heartbeat={hb} />
                ))}
              </div>
            </>
          )}
        </div>
    </div>
  )
}
